import {CommonModule} from '@angular/common';
import {Component, inject, output, OutputEmitterRef, signal, WritableSignal} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from '@angular/material/autocomplete';
import {MatFormField, MatInput, MatSuffix} from '@angular/material/input';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {
  ButtonType,
  CovButtonComponent,
  CovIconComponent,
  CovToastService,
  IconName,
} from '@cov/ui-angular';
import {translate, TranslocoPipe} from '@jsverse/transloco';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {ListRequest} from '../../../../interfaces/list-request.interface';
import {
  AUTOCOMPLETE_DEBOUNCE_TIME,
  AUTOCOMPLETE_MINIMUM_CHARACTER,
  PAGE_SIZE_INFINITY,
} from '../../../../shared/constants';
import {Comparison} from '../../../../shared/enum/comparision.enum';
import {SidePanelGridComponent} from '../../../../shared/ui/side-panel-grid/side-panel-grid.component';
import {ORDER_NUMBER} from '../../../orders/constants/names';
import {OrderDetails} from '../../../orders/data-access/interfaces/order-details.interface';
import {OrdersService} from '../../../orders/services/orders.service';
import {PageId} from '../../enum/inquiry-page.enum';
import {AbstractBlendingPageComponent} from '../abstract-blending-page/abstract-blending-page.component';
import {LEADING_ZERO_REGEX} from '../../../../shared/constants/regex';
import {SearchService} from '../../../search/service/search.service';
import {Filter} from '../../../orders/data-access/interfaces/filter.interface';
import {ReportLineDetailData} from '../../../../interfaces/report-line-details.interface';

@Component({
  selector: 'cov-inquiry-order-select-panel',
  imports: [
    CommonModule,
    SidePanelGridComponent,
    TranslocoPipe,
    CovButtonComponent,
    MatFormField,
    MatInput,
    CovIconComponent,
    MatSuffix,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatProgressSpinner,
    ReactiveFormsModule,
    MatOption,
  ],
  templateUrl: './inquiry-order-select-panel.component.html',
  styleUrl: './inquiry-order-select-panel.component.scss',
})
export class InquiryOrderSelectPanelComponent extends AbstractBlendingPageComponent {
  protected readonly ButtonType = ButtonType;
  protected readonly IconName = IconName;
  protected readonly PageId = PageId;

  // INJECTS
  protected ordersService: OrdersService = inject(OrdersService);
  private toastService: CovToastService = inject(CovToastService);
  private searchService = inject(SearchService);

  //OUTPUTS
  public cancelOrderSelect: OutputEmitterRef<PageId> = output();
  public selectedOrder: OutputEmitterRef<ReportLineDetailData> = output();

  // PROPS
  protected searchInput: FormControl = new FormControl();

  // STATE
  private deactivated$: Subject<void> = new Subject();
  protected loading: WritableSignal<boolean> = signal(false);

  protected filteredOrders$: Observable<OrderDetails[]> = this.searchInput.valueChanges.pipe(
    takeUntil(this.deactivated$),
    debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME),
    distinctUntilChanged(),
    filter((value) => typeof value === 'string' && value.length >= AUTOCOMPLETE_MINIMUM_CHARACTER),
    switchMap((term) => {
      this.loading.set(true);
      return this.searchService
        .search(this.buildSearchFilters(term))
        .pipe(map((searchResult) => searchResult.groups.flatMap((group) => group.reports)));
    }),
    tap(() => this.loading.set(false)),
  );

  public constructor() {
    super();
  }

  private buildSearchFilters(term: string): ListRequest {
    return {
      currentPage: 0,
      pageSize: PAGE_SIZE_INFINITY,
      sort: ORDER_NUMBER,
      sortDirection: 'asc',
      filters: [
        {
          name: ORDER_NUMBER,
          value: term,
          comparisonType: Comparison.CONTAINS,
        } as Filter,
      ],
      mode: 'BASIC',
    };
  }

  public selectOrder(): void {
    if (!this.searchInput.value || typeof this.searchInput.value === 'string') {
      this.toastService.error(
        translate('inquiries.sideDrawer.orderSelectPanel.notifications.noOrderSelected'),
      );
      return;
    }
    const order = this.searchInput.value as OrderDetails;
    this.ordersService
      .loadReportLineDetail(order.orderNumber, order.orderPosition, false)
      .subscribe((response) => this.selectedOrder.emit(response.reportLine));
  }

  protected displayOrder(order: OrderDetails | null): string {
    return order
      ? `${order.orderNumber} / ${order.orderPosition.replace(LEADING_ZERO_REGEX, '')}`
      : '';
  }

  public override onDeactivated(): void {
    this.deactivated$.next();
  }
}
